import classNames from 'classnames';
import { HTMLRenderer } from 'common/html-renderer.js';
import { ImageWrapper } from 'common/image/image-wrapper.js';
import { LinkFactoryReactRouter } from 'common/link/link-react-router.js';
import { TileBodyHeaderV2 } from 'common/tile/tile-body-header-v2.js';
import { Tile } from 'common/tile/tile.js';
import { Component } from 'react';
import { IModule } from 'redux-state/layout/types.js';
import { formatDate } from 'utilities/date.js';
import { makeRequest } from 'utilities/fetch/api-gateway.js';

export enum ItemType {
  Tweets = 'tweets',
  BlogPosts = 'blog-posts',
}

export interface IItemProp {
  timestamp: string;
  title?: string;
  content: string;
  author: string;
  url?: string;
  image: string;
  featured?: boolean;
}

interface IListWithFeatureProps extends IModule<IListWithFeatureConfig> {
  isDrawer?: boolean;
}

interface IListWithFeatureConfig {
  items: string | IItemProp[];
  itemType: ItemType;
}

interface IState {
  itemsSet: boolean;
  formattedItems: IItemProp[] | undefined;
}

const TWITTER_DATE_FORMAT = `m:hh aaaaa'm' - dd MMM yyyy`;
const BLOG_DATE_FORMAT = 'dd MMM yyyy';
export class ListWithFeature extends Component<IListWithFeatureProps, IState> {
  constructor(props: IListWithFeatureProps) {
    super(props);

    const {
      config: { items },
    } = props;
    const mustRequestItems = typeof items === 'string';

    this.state = {
      itemsSet: !mustRequestItems,
      formattedItems: mustRequestItems ? undefined : (items as IItemProp[]),
    };
  }

  override componentDidMount() {
    const {
      config: { items },
    } = this.props;

    /*
     * This module has its `items` attribute either being:
     * 1. An array with the posts/articles to be shown
     * 2. A URL to request the content from.
     *
     * What's below handles both cases and sets `state.formattedItems`
     * depending on what's available.
     */
    if (typeof items === 'string' && !this.state.itemsSet) {
      return makeRequest<IItemProp[] | undefined>({ path: items, apiPrefix: false })
        .then((results: IItemProp[] | undefined) => {
          this.setState({
            formattedItems: results, // Same structure as we expect coming from the new endpoint. :)
            itemsSet: true,
          });
        })
        .catch(() => {
          this.setState({
            itemsSet: true,
          });
        });
    }
  }

  override render() {
    if (!this.state.itemsSet) {
      return null;
    }

    const {
      config: { itemType },
      isDrawer,
    } = this.props;

    const { formattedItems } = this.state;

    let content;

    if (!formattedItems) {
      content = <p>Sorry, this feed is not currently available. Please try again later</p>;
    } else {
      const featureItem = formattedItems.find((item: IItemProp) => !!item.featured);
      const listOfItems = formattedItems.filter((i) => !i.featured);

      const listClasses = classNames('ListWithFeature', { 'u-mT-md': !isDrawer });
      const gridClasses = classNames('Grid-cell u-width1of1', { 'u-md-width7of12': !!featureItem });

      content = (
        <div className={listClasses}>
          <div className="Grid Grid--withGutter">
            {/* Feature */}
            {featureItem && (
              <div className="Grid-cell u-width1of1 u-md-width5of12">
                <div className="ListWithFeature-item ListWithFeature-item--feature">
                  {this.renderListItem(featureItem, itemType)}
                </div>
              </div>
            )}

            {/* Other tweets */}
            <div className={gridClasses}>
              {listOfItems.map((item, index) => {
                return (
                  <div className="ListWithFeature-item" key={index}>
                    {this.renderListItem(item, itemType)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <Tile dataModule="list-with-feature" {...this.props}>
        <div className="Tile-body">
          <TileBodyHeaderV2 {...this.props} />

          {content}
        </div>
      </Tile>
    );
  }

  renderListItem(listItem: IItemProp, itemType: ItemType) {
    const { timestamp, title, content, author, url, image } = listItem;

    const twitterDate = timestamp && formatDate(timestamp, TWITTER_DATE_FORMAT);
    const blogDate = timestamp && formatDate(timestamp, BLOG_DATE_FORMAT);

    const isFromBlog = itemType === ItemType.BlogPosts;
    const isFromTwitter = itemType === ItemType.Tweets;

    return (
      <>
        {image && (
          <div className="ListWithFeature-item-image">
            <ImageWrapper src={image} ratio="square" rounded shadow />
          </div>
        )}
        <div className="ListWithFeature-item-content">
          {isFromBlog && (
            <>
              <HTMLRenderer content={title || ''} classes="h2 u-mT-0 u-mB-0" />
              <p className="fineprint u-textGrey u-mT-0 u-mB-xs">
                {blogDate} | by {author}
              </p>
            </>
          )}
          <HTMLRenderer content={content || ''} classes="u-mT-0 u-mB-0" />
          {isFromTwitter && url && (
            <div>
              <LinkFactoryReactRouter href={url} className="fineprint u-linkClean u-md-mR-xs">
                @{author}
              </LinkFactoryReactRouter>
              <span className="fineprint u-sm-block u-textGrey">{twitterDate}</span>
            </div>
          )}
        </div>
      </>
    );
  }
}
